import { useEffect, useState } from 'react'
import { Control, FieldValues, Path, useFormState, useWatch } from 'react-hook-form'
import { FormHelperText } from '@mui/material'
import { getValueByNameStr } from '../../../../utils/objectUtil'
import { GContainer, GItem } from '../../common/grids'
import { NumberBox } from '../../common/inputs/numberBox'
import { translate } from '../../../../i18n'

const PADDING = '0 8px'

interface DecimalTypeFieldProps<T extends FieldValues> {
  name: string
  label: string
  unit: string
  required: boolean
  control: Control<T>
  intMax?: number
  intMin?: number
  intPlaceholder?: number
  fracMax?: number
  fracMin?: number
  fracPlaceholder?: number
  mt?: number
}

const DecimalTypeField = <T extends FieldValues>(props: DecimalTypeFieldProps<T>) => {
  const { name, label, unit, required, control, intMax, intMin, intPlaceholder, fracMax, fracMin, fracPlaceholder } =
    props
  const [isIntegerRequired, setIsIntegerRequired] = useState(required)

  const intName = `${name}IntPart`
  const fracName = `${name}FracPart`

  const { errors } = useFormState({ control })
  const watchFractional = useWatch({
    name: fracName as unknown as Path<T>,
    control,
  })

  const integerError = getValueByNameStr(errors, intName)
  const fractionalError = getValueByNameStr(errors, fracName)

  useEffect(() => {
    if (required) return
    if (watchFractional) {
      setIsIntegerRequired(true)
      return
    }
    if (!required) {
      setIsIntegerRequired(false)
    }
  }, [watchFractional])

  return (
    <GContainer alignItems="flex-end">
      <GContainer alignItems="flex-end">
        <GItem xs="auto">
          <NumberBox
            name={intName}
            label={`${label}（整数部分）`}
            max={intMax}
            min={intMin}
            placeholder={intPlaceholder?.toString()}
            control={control}
            required={isIntegerRequired ?? required}
          />
        </GItem>
        <GItem xs={0.1} style={{ display: 'flex', alignItems: 'center', padding: PADDING }}>
          .
        </GItem>
        <GItem xs="auto">
          <NumberBox
            name={fracName}
            label={`${label}（小数部分）`}
            max={fracMax}
            min={fracMin}
            placeholder={fracPlaceholder?.toString()}
            control={control}
            required={isIntegerRequired ?? required}
          />
        </GItem>
        <GItem xs={0.1} style={{ display: 'flex', alignItems: 'center', padding: PADDING }}>
          {translate(unit)}
        </GItem>
      </GContainer>
      <GContainer alignItems="flex-end">
        {integerError && <FormHelperText error>{integerError.message}</FormHelperText>}
        {fractionalError && <FormHelperText error>少数部分は{fractionalError.message}</FormHelperText>}
      </GContainer>
    </GContainer>
  )
}

export default DecimalTypeField
